/**
 * Notifications Service
 * Handles all notification-related API calls
 */

import { apiService, ApiResponse } from './api.service';

/**
 * Notification Type Enum
 */
export type NotificationType = 'ORDER' | 'INVENTORY' | 'PROMOTION' | 'SYSTEM' | 'ALERT';

/**
 * Notification Interface
 */
export interface Notification {
  id: string;
  title: string;
  message: string;
  type: NotificationType;
  isRead: boolean;
  isArchived?: boolean;
  userId?: string;
  tenantId: string;
  data?: Record<string, any>;
  createdAt: string;
  readAt?: string;
}

/**
 * Create Notification DTO
 */
export interface CreateNotificationDto {
  title: string;
  message: string;
  type: NotificationType;
  userId?: string;
  metadata?: Record<string, any>;
}

export interface RequestStatusChangeApprovalDto {
  orderId: string;
  orderNumber: string;
  requestedStatus: 'REJECTED' | 'CANCELLED' | 'REFUNDED';
}

/**
 * Notifications Query Parameters
 */
export interface NotificationsQuery {
  page?: number;
  limit?: number;
  type?: NotificationType;
  unreadOnly?: boolean;
}

/**
 * Paginated Notifications Response
 */
export interface PaginatedNotifications {
  data: Notification[];
  meta: {
    total: number;
    unreadCount: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Notifications Service
 */
class NotificationsService {
  /**
   * Get all notifications with pagination and filters
   */
  async getNotifications(query?: NotificationsQuery): Promise<ApiResponse<PaginatedNotifications>> {
    try {
      const params = new URLSearchParams();
      
      if (query?.page) params.append('page', query.page.toString());
      if (query?.limit) params.append('limit', query.limit.toString());
      if (query?.type) params.append('type', query.type);
      if (query?.unreadOnly !== undefined) params.append('unreadOnly', query.unreadOnly.toString());

      const queryString = params.toString() ? `?${params.toString()}` : '';
      return await apiService.get<PaginatedNotifications>(`/notifications${queryString}`);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      throw error;
    }
  }

  /**
   * Get single notification by ID
   */
  async getNotificationById(id: string): Promise<ApiResponse<Notification>> {
    try {
      return await apiService.get<Notification>(`/notifications/${id}`);
    } catch (error) {
      console.error('Failed to fetch notification:', error);
      throw error;
    }
  }

  /**
   * Create new notification
   */
  async createNotification(data: CreateNotificationDto): Promise<ApiResponse<Notification>> {
    try {
      return await apiService.post<Notification>('/notifications', data);
    } catch (error) {
      console.error('Failed to create notification:', error);
      throw error;
    }
  }

  /**
   * Request admin/manager approval for sensitive order status changes
   */
  async requestStatusChangeApproval(data: RequestStatusChangeApprovalDto): Promise<ApiResponse<{ success: boolean; sentTo: number }>> {
    try {
      return await apiService.post<{ success: boolean; sentTo: number }>('/notifications/request-status-change-approval', data);
    } catch (error) {
      console.error('Failed to request status change approval:', error);
      throw error;
    }
  }

  /**
   * Approve a pending status change request notification
   */
  async approveStatusChangeRequest(notificationId: string): Promise<ApiResponse<{ success: boolean; orderId: string; requestedStatus: string }>> {
    try {
      return await apiService.patch<{ success: boolean; orderId: string; requestedStatus: string }>(`/notifications/${notificationId}/approve-status-change`);
    } catch (error) {
      console.error('Failed to approve status change request:', error);
      throw error;
    }
  }

  /**
   * Deny a pending status change request notification
   */
  async denyStatusChangeRequest(notificationId: string): Promise<ApiResponse<{ success: boolean }>> {
    try {
      return await apiService.patch<{ success: boolean }>(`/notifications/${notificationId}/deny-status-change`);
    } catch (error) {
      console.error('Failed to deny status change request:', error);
      throw error;
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(id: string): Promise<ApiResponse<Notification>> {
    try {
      return await apiService.patch<Notification>(`/notifications/${id}/read`);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<ApiResponse<void>> {
    try {
      return await apiService.patch<void>('/notifications/read-all');
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Delete notification
   */
  async deleteNotification(id: string): Promise<ApiResponse<void>> {
    try {
      return await apiService.delete<void>(`/notifications/${id}`);
    } catch (error) {
      console.error('Failed to delete notification:', error);
      throw error;
    }
  }

  /**
   * Delete all read notifications
   */
  async deleteAllRead(): Promise<ApiResponse<void>> {
    try {
      return await apiService.delete<void>('/notifications');
    } catch (error) {
      console.error('Failed to delete read notifications:', error);
      throw error;
    }
  }

  /**
   * Get unread count
   */
  async getUnreadCount(): Promise<ApiResponse<{ count: number }>> {
    try {
      return await apiService.get<{ count: number }>('/notifications/unread-count');
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const notificationsService = new NotificationsService();
